import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Spinner,
  Card,
  CardBody,
  Flex,
  InputGroup,
  InputLeftElement,
  Input,
  Select,
  Avatar,
  Tooltip,
  Container,
  SimpleGrid,
  Divider
} from '@chakra-ui/react';
import {
  FiPlus,
  FiMoreVertical,
  FiTrash2,
  FiSearch,
  FiArrowLeft,
  FiMusic,
  FiRepeat
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../services/axios';
import { useAuth } from '../../../hooks/useAuth';
import { useWalletContext } from '../../../context/WalletContext';
import ArtistSwitchConfirmation from './ArtistSwitchConfirmation';

/**
 * Helper function to extract error message from API response
 * Handles FastAPI validation errors which come as arrays of objects
 */
const getErrorMessage = (error) => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    // If detail is an array (FastAPI validation errors)
    if (Array.isArray(detail)) {
      return detail.map(err => err.msg || JSON.stringify(err)).join(', ');
    }
    // If detail is a string
    if (typeof detail === 'string') {
      return detail;
    }
    // If detail is an object
    if (typeof detail === 'object') {
      return detail.msg || JSON.stringify(detail);
    }
  }
  // Fallback to message or generic error
  return error.response?.data?.message || error.message || 'An error occurred';
};

const ArtistList = () => {
  const [artists, setArtists] = useState([]);
  const [filteredArtists, setFilteredArtists] = useState([]);
  const [merchantData, setMerchantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [artistToDelete, setArtistToDelete] = useState(null);
  const [artistToSwitch, setArtistToSwitch] = useState(null);
  const [switching, setSwitching] = useState(false);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isSwitchOpen, onOpen: onSwitchOpen, onClose: onSwitchClose } = useDisclosure();
  const [deleteLoading, setDeleteLoading] = useState(false);

  const navigate = useNavigate();
  const toast = useToast();
  const { isSwitchedContext, switchToArtist } = useAuth();
  const { refreshWallet } = useWalletContext();
  const deleteCancelRef = React.useRef();
  const switchCancelRef = React.useRef();


  // Block access to artist management when switched to artist context
  useEffect(() => {
    if (isSwitchedContext) {
      navigate('/dashboard/artist/tracks', { replace: true });
      toast({
        title: 'Access Restricted',
        description: 'You are currently viewing as an artist. Use "Switch back to Merchant" to manage artists.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [isSwitchedContext, navigate, toast]);

  useEffect(() => {
    fetchMerchantData();
    fetchArtists();
  }, []);

  useEffect(() => {
    filterArtists();
  }, [artists, searchTerm, statusFilter]);

  const fetchMerchantData = async () => {
    try {
      const response = await axiosInstance.get('/merchants/profile');
      setMerchantData(response.data);
    } catch (error) {
      console.error('Error fetching merchant data:', error);
    }
  };

  const fetchArtists = async () => {
    try {
      const response = await axiosInstance.get('/merchants/artists');
      setArtists(response.data);
    } catch (error) {
      console.error('Error fetching artists:', error);
      toast({
        title: 'Error',
        description: 'Failed to load artists',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterArtists = () => {
    let filtered = artists;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(artist =>
        artist.artist_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        artist.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (artist.username && artist.username.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(artist => artist.account_status === statusFilter);
    }

    setFilteredArtists(filtered);
  };

  const handleCreateArtist = () => {
    navigate('/merchant/artists/create');
  };

  const handleSwitchToArtist = (artist) => {
    setArtistToSwitch(artist);
    onSwitchOpen();
  };

  const confirmSwitchToArtist = async () => {
    if (!artistToSwitch) return;

    setSwitching(true);
    try {
      const artistId = artistToSwitch.artist_id || artistToSwitch.user_id || artistToSwitch.id;
      await switchToArtist(artistId, artistToSwitch);

      // Refresh wallet data for new context
      await refreshWallet(true);

      // Redirect to artist dashboard
      navigate('/dashboard/artist/tracks');

      toast({
        title: `Switched to ${artistToSwitch.artist_name}`,
        description: 'You are now viewing the artist perspective',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error switching to artist:', error);
      toast({
        title: 'Switch Failed',
        description: `Failed to switch to ${artistToSwitch.artist_name}`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSwitching(false);
      setArtistToSwitch(null);
      onSwitchClose();
    }
  };

  const handleDeleteArtist = (artist) => {
    setArtistToDelete(artist);
    onDeleteOpen();
  };

  const confirmDeleteArtist = async () => {
    if (!artistToDelete) return;

    setDeleteLoading(true);
    try {
      await axiosInstance.delete(`/merchants/artists/${artistToDelete.artist_id}`);

      toast({
        title: 'Artist Deleted',
        description: `${artistToDelete.artist_name} has been removed from your artists`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Remove from local state
      setArtists(prev => prev.filter(a => a.artist_id !== artistToDelete.artist_id));

    } catch (error) {
      console.error('Error deleting artist:', error);
      toast({
        title: 'Delete Failed',
        description: getErrorMessage(error) || 'Failed to delete artist',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDeleteLoading(false);
      setArtistToDelete(null);
      onDeleteClose();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'gray';
      case 'suspended': return 'red';
      default: return 'gray';
    }
  };

  const getVerificationColor = (status) => {
    switch (status) {
      case 'verified': return 'green';
      case 'pending': return 'yellow';
      case 'rejected': return 'red';
      default: return 'gray';
    }
  };

  if (loading) {
    return (
      <Container maxW="7xl" centerContent py={20}>
        <VStack spacing={4}>
          <Spinner size="xl" color="dark.900" thickness="3px" />
          <Text color="primary.900" fontSize="lg">Loading your artists...</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="7xl" py={{ base: 4, md: 8 }} px={{ base: 4, md: 6 }}>
      <VStack spacing={{ base: 6, md: 8 }} align="stretch">
        {/* Header */}
        <Box>
          <Flex
            align={{ base: "start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={{ base: 4, md: 0 }}
          >
            <VStack align="start" spacing={1} flex={1}>
              <Button
                leftIcon={<Icon as={FiArrowLeft} />}
                variant="ghost"
                size="sm"
                onClick={() => navigate('/merchant/dashboard')}
                color="primary.900"
                _hover={{ bg: "dark.900", color: "primary.100" }}
                alignSelf="flex-start"
              >
                Back to Dashboard
              </Button>
              <Heading size="xl" color="dark.900" fontFamily="heading">
                Manage Artists
              </Heading>
              <Text color="primary.900" fontSize="lg">
                {artists.length} total artists in your portfolio
              </Text>
            </VStack>
            <Tooltip
              label="Your merchant account must be verified before you can create linked artists"
              isDisabled={merchantData?.verification_status === 'verified'}
              hasArrow
              placement="bottom"
            >
              <Button
                leftIcon={<Icon as={FiPlus} />}
                variant="brand"
                size={{ base: "md", md: "lg" }}
                onClick={handleCreateArtist}
                isDisabled={merchantData?.verification_status !== 'verified'}
                w={{ base: "full", md: "auto" }}
                _hover={{
                  transform: merchantData?.verification_status === 'verified' ? "translateY(-1px)" : "none",
                  boxShadow: merchantData?.verification_status === 'verified' ? "lg" : "none"
                }}
                transition="all 0.2s"
                opacity={merchantData?.verification_status !== 'verified' ? 0.6 : 1}
              >
                Add New Artist
              </Button>
            </Tooltip>
          </Flex>
          <Divider borderColor="secondary.900" mt={6} />
        </Box>

        {/* Filters and Search */}
        <Card bg="dark.500" borderColor="secondary.900" borderWidth="1px" shadow="lg">
          <CardBody p={{ base: 4, md: 6 }}>
            <VStack spacing={{ base: 4, md: 0 }} align="stretch">
              <HStack spacing={4} wrap={{ base: 'wrap', md: 'nowrap' }} justify="space-between">
                <InputGroup maxW={{ base: "full", md: "300px" }}>
                  <InputLeftElement>
                    <Icon as={FiSearch} color="primary.900" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search artists..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    bg="secondary.900"
                    borderColor="secondary.900"
                    color="primary.100"
                    _placeholder={{ color: "primary.900" }}
                    _focus={{ borderColor: "dark.900" }}
                  />
                </InputGroup>

                <Select
                  placeholder="All Statuses"
                  maxW={{ base: "full", md: "200px" }}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  bg="secondary.900"
                  borderColor="secondary.900"
                  color="primary.100"
                  _focus={{ borderColor: "dark.900" }}
                >
                  <option value="all" style={{ background: '#1A1A1A', color: '#E2E8F0' }}>All Statuses</option>
                  <option value="active" style={{ background: '#1A1A1A', color: '#E2E8F0' }}>Active</option>
                  <option value="inactive" style={{ background: '#1A1A1A', color: '#E2E8F0' }}>Inactive</option>
                  <option value="suspended" style={{ background: '#1A1A1A', color: '#E2E8F0' }}>Suspended</option>
                </Select>

                <Text fontSize="sm" color="primary.900" minW="fit-content" alignSelf="center">
                  {filteredArtists.length} of {artists.length} artists
                </Text>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Artists Content */}
        <Card bg="dark.500" borderColor="secondary.900" borderWidth="1px" shadow="lg">
          <CardBody p={{ base: 4, md: 6 }}>
            {filteredArtists.length === 0 ? (
              <Box textAlign="center" py={12}>
                <Box
                  p={6}
                  rounded="full"
                  bg="secondary.900"
                  display="inline-flex"
                  mb={4}
                >
                  <Icon as={FiMusic} boxSize={8} color="dark.900" />
                </Box>
                <Text color="primary.900" mb={6} fontSize="lg">
                  {artists.length === 0
                    ? "You haven't created any artists yet"
                    : "No artists match your search criteria"
                  }
                </Text>
                {artists.length === 0 && (
                  <Button
                    leftIcon={<Icon as={FiPlus} />}
                    variant="brand"
                    size="lg"
                    onClick={handleCreateArtist}
                    _hover={{
                      transform: "translateY(-1px)",
                      boxShadow: "lg"
                    }}
                    transition="all 0.2s"
                  >
                    Create Your First Artist
                  </Button>
                )}
              </Box>
            ) : (
              <Box>
                {/* Mobile Card Layout */}
                <Box display={{ base: "block", lg: "none" }}>
                  <SimpleGrid columns={1} spacing={4}>
                    {filteredArtists.map((artist) => (
                      <Card
                        key={artist.artist_id}
                        bg="secondary.900"
                        borderColor="secondary.700"
                        borderWidth="1px"
                        _hover={{
                          bg: "secondary.700",
                          transform: "translateY(-1px)",
                          shadow: "md"
                        }}
                        transition="all 0.2s"
                      >
                        <CardBody p={4}>
                          <VStack align="stretch" spacing={3}>
                            <Flex justify="space-between" align="start">
                              <HStack spacing={3} flex={1}>
                                <Avatar
                                  size="md"
                                  name={artist.artist_name}
                                  bg="dark.900"
                                  color="primary.100"
                                />
                                <VStack align="start" spacing={1} flex={1}>
                                  <Text fontWeight="bold" fontSize="md" color="primary.100">
                                    {artist.artist_name}
                                  </Text>
                                  {artist.username && (
                                    <Text fontSize="sm" color="primary.900">
                                      @{artist.username}
                                    </Text>
                                  )}
                                  <Text fontSize="sm" color="primary.900">
                                    {artist.email}
                                  </Text>
                                </VStack>
                              </HStack>
                              <Menu>
                                <MenuButton
                                  as={IconButton}
                                  icon={<Icon as={FiMoreVertical} />}
                                  variant="ghost"
                                  size="sm"
                                  color="primary.900"
                                  _hover={{ bg: "secondary.700" }}
                                  aria-label="Artist actions"
                                />
                                <MenuList bg="secondary.900" borderColor="dark.900">
                                  <MenuItem
                                    icon={<Icon as={FiRepeat} />}
                                    onClick={() => handleSwitchToArtist(artist)}
                                    bg="secondary.900"
                                    color="primary.100"
                                    _hover={{ bg: "secondary.700", color: "primary.100" }}
                                    isDisabled={switching}
                                  >
                                    Switch to Artist
                                  </MenuItem>
                                  <MenuItem
                                    icon={<Icon as={FiTrash2} />}
                                    color="red.400"
                                    onClick={() => handleDeleteArtist(artist)}
                                    bg="secondary.900"
                                    _hover={{ bg: "red.500", color: "white" }}
                                    isDisabled={switching}
                                  >
                                    Delete Artist
                                  </MenuItem>
                                </MenuList>
                              </Menu>
                            </Flex>

                            <HStack justify="space-between" wrap="wrap" spacing={2}>
                              <Badge
                                colorScheme={getStatusColor(artist.account_status)}
                                textTransform="capitalize"
                                size="sm"
                              >
                                {artist.account_status}
                              </Badge>
                              <Badge
                                colorScheme={getVerificationColor(artist.verification_status)}
                                textTransform="capitalize"
                                size="sm"
                              >
                                {artist.verification_status}
                              </Badge>
                              <Text fontSize="xs" color="primary.900">
                                {new Date(artist.created_at).toLocaleDateString()}
                              </Text>
                            </HStack>
                          </VStack>
                        </CardBody>
                      </Card>
                    ))}
                  </SimpleGrid>
                </Box>

                {/* Desktop Table Layout */}
                <Box display={{ base: "none", lg: "block" }} overflowX="auto">
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th color="primary.900" borderColor="secondary.900">Artist</Th>
                        <Th color="primary.900" borderColor="secondary.900">Email</Th>
                        <Th color="primary.900" borderColor="secondary.900">Status</Th>
                        <Th color="primary.900" borderColor="secondary.900">Verification</Th>
                        <Th color="primary.900" borderColor="secondary.900">Created</Th>
                        <Th color="primary.900" borderColor="secondary.900" width="50px"></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredArtists.map((artist) => (
                        <Tr key={artist.artist_id} _hover={{ bg: 'secondary.700' }}>
                          <Td borderColor="secondary.900">
                            <HStack spacing={3}>
                              <Avatar
                                size="sm"
                                name={artist.artist_name}
                                bg="dark.900"
                                color="primary.100"
                              />
                              <VStack align="start" spacing={0}>
                                <Text fontWeight="medium" fontSize="sm" color="primary.100">
                                  {artist.artist_name}
                                </Text>
                                {artist.username && (
                                  <Text fontSize="xs" color="primary.900">
                                    @{artist.username}
                                  </Text>
                                )}
                              </VStack>
                            </HStack>
                          </Td>

                          <Td borderColor="secondary.900">
                            <Text fontSize="sm" color="primary.900">
                              {artist.email}
                            </Text>
                          </Td>

                          <Td borderColor="secondary.900">
                            <Badge
                              colorScheme={getStatusColor(artist.account_status)}
                              textTransform="capitalize"
                            >
                              {artist.account_status}
                            </Badge>
                          </Td>

                          <Td borderColor="secondary.900">
                            <Badge
                              colorScheme={getVerificationColor(artist.verification_status)}
                              textTransform="capitalize"
                            >
                              {artist.verification_status}
                            </Badge>
                          </Td>

                          <Td borderColor="secondary.900">
                            <Tooltip label={new Date(artist.created_at).toLocaleString()}>
                              <Text fontSize="sm" color="primary.900">
                                {new Date(artist.created_at).toLocaleDateString()}
                              </Text>
                            </Tooltip>
                          </Td>

                          <Td borderColor="secondary.900">
                            <Menu>
                              <MenuButton
                                as={IconButton}
                                icon={<Icon as={FiMoreVertical} />}
                                variant="ghost"
                                size="sm"
                                color="primary.900"
                                _hover={{ bg: "secondary.700" }}
                                aria-label="Artist actions"
                              />
                              <MenuList bg="secondary.900" borderColor="dark.900">
                                <MenuItem
                                  icon={<Icon as={FiRepeat} />}
                                  onClick={() => handleSwitchToArtist(artist)}
                                  bg="secondary.900"
                                  color="primary.100"
                                  _hover={{ bg: "secondary.700", color: "primary.100" }}
                                  isDisabled={switching}
                                >
                                  Switch to Artist
                                </MenuItem>
                                <MenuItem
                                  icon={<Icon as={FiTrash2} />}
                                  color="red.400"
                                  onClick={() => handleDeleteArtist(artist)}
                                  bg="secondary.900"
                                  _hover={{ bg: "red.500", color: "white" }}
                                  isDisabled={switching}
                                >
                                  Delete Artist
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={deleteCancelRef}
        onClose={onDeleteClose}
        isCentered
      >
        <AlertDialogOverlay bg="rgba(0, 0, 0, 0.7)" backdropFilter="blur(2px)">
          <AlertDialogContent
            bg="dark.500"
            borderColor="secondary.900"
            borderWidth="1px"
            shadow="xl"
            mx={4}
            maxW="md"
          >
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="primary.100">
              Delete Artist
            </AlertDialogHeader>

            <AlertDialogBody py={4}>
              <Text fontSize="sm" color="primary.100">
                Are you sure you want to delete{' '}
                <Text as="span" fontWeight="bold" color="white">
                  {artistToDelete?.artist_name}
                </Text>?
              </Text>
              <Text fontSize="sm" color="primary.100" mt={2}>
                This action cannot be undone and will remove all associated data.
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <HStack spacing={3}>
                <Button
                  ref={deleteCancelRef}
                  onClick={onDeleteClose}
                  isDisabled={deleteLoading}
                  variant="ghost"
                  color="primary.100"
                  _hover={{ bg: "secondary.900", color: "white" }}
                >
                  Cancel
                </Button>
                <Button
                  bg="red.500"
                  color="white"
                  _hover={{ bg: "red.600", transform: "translateY(-1px)", boxShadow: "lg" }}
                  onClick={confirmDeleteArtist}
                  isLoading={deleteLoading}
                  loadingText="Deleting..."
                  transition="all 0.2s"
                >
                  Delete
                </Button>
              </HStack>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Switch Confirmation Dialog */}
      <ArtistSwitchConfirmation
        isOpen={isSwitchOpen}
        onClose={onSwitchClose}
        artist={artistToSwitch}
        onConfirm={confirmSwitchToArtist}
        isLoading={switching}
        cancelRef={switchCancelRef}
      />
    </Container>
  );
};

export default ArtistList;